import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronDown, Zap, Target, Trophy } from 'lucide-react';
import { Button, CardCorner, AnimatedBorder, TranslatedText, RegistrationWizard, IniestaGlitch } from '../ui';
import { LoginModal } from '../ui/LoginModal';
import { useVideoMode } from '../../context';

export function Hero() {
  const { t } = useTranslation();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);
  const { isVideoMode, isTransitioning } = useVideoMode();
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);

  const isHidden = isVideoMode || isTransitioning;

  const stats = [
    { icon: Target, value: '6', label: t('hero.statCountries'), delay: 0 },
    { icon: Trophy, value: '29+', label: t('hero.statTeams'), delay: 0.1 },
    { icon: Zap, value: '30+', label: t('hero.statStories'), delay: 0.2 },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
    exit: {
      opacity: 0,
      transition: { staggerChildren: 0.05, staggerDirection: -1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] as const },
    },
    exit: { 
      opacity: 0, 
      y: -30,
      transition: { duration: 0.4, ease: 'easeIn' as const },
    },
  };

  // Explode animation for text characters (keeping for future use)
  const _explodeVariants = {
    visible: { 
      opacity: 1, 
      scale: 1, 
      x: 0, 
      y: 0,
      rotate: 0,
      filter: 'blur(0px)',
    },
    exit: (i: number) => ({ 
      opacity: 0, 
      scale: 0.5 + Math.random() * 0.5,
      x: (Math.random() - 0.5) * 200,
      y: (Math.random() - 0.5) * 200,
      rotate: (Math.random() - 0.5) * 90,
      filter: 'blur(4px)',
      transition: { 
        duration: 0.6, 
        delay: i * 0.02,
        ease: [0.22, 1, 0.36, 1] as const,
      },
    }),
  };
  // Suppress unused variable warning
  void _explodeVariants;

  return (
    <section className="relative min-h-screen flex items-center overflow-x-hidden bg-transparent pointer-events-none">
      {/* Gradient overlays - transparent to let global 3D through */}
      <div className="absolute inset-0 bg-gradient-radial from-brand-yellow/5 via-transparent to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-brand-darkNavy/80 to-transparent" />

      {/* Content */}
      <AnimatePresence mode="wait">
        {!isHidden && (
          <motion.div 
            key="hero-content"
            style={{ y, opacity }}
            className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 select-none"
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <motion.div
              variants={containerVariants}
              className="w-full"
            >
              {/* Two column layout - Text left, Iniesta right */}
              <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-12">
                {/* Left column - Text content */}
                <div className="flex-1 text-center lg:text-left">
                  {/* Top badge */}
                  <motion.div variants={itemVariants} className="flex justify-center lg:justify-start mb-4 sm:mb-8 pointer-events-auto">
                    <AnimatedBorder>
                      <div className="px-6 py-2 flex items-center gap-2">
                        <Zap className="w-4 h-4 text-brand-yellow" />
                        <span className="text-sm font-medium text-dark-300">
                          {t('hero.badge')}
                        </span>
                      </div>
                    </AnimatedBorder>
                  </motion.div>

                  {/* Main Title - FIFA Style */}
                  <motion.div variants={itemVariants} className="mb-4 sm:mb-8">
                    <h1 className="relative inline-block">
                      {/* Background glow - teal for title */}
                      <div className="absolute inset-0 blur-3xl bg-fifa-teal/15 scale-150" />
                      
                      <div className="relative space-y-2">
                        <motion.span 
                          className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black tracking-tighter text-neon-teal"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.8, delay: 0.2 }}
                        >
                          <TranslatedText 
                            i18nKey="hero.title1" 
                            type="digital"
                            duration={0.6}
                            className="font-black tracking-tighter"
                          />
                        </motion.span>
                        
                        <motion.span 
                          className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter text-white"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.8, delay: 0.4 }}
                        >
                          <TranslatedText 
                            i18nKey="hero.title2" 
                            type="digital"
                            duration={0.7}
                            delay={0.1}
                            className="font-black tracking-tighter"
                          />
                        </motion.span>
                        
                        <motion.span 
                          className="block text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter text-white"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.8, delay: 0.6 }}
                        >
                          <TranslatedText 
                            i18nKey="hero.title3" 
                            type="digital"
                            duration={0.8}
                            delay={0.2}
                            className="font-black tracking-tighter"
                          />
                        </motion.span>
                      </div>
                    </h1>
                  </motion.div>

                  {/* Subtitle */}
                  <motion.p
                    variants={itemVariants}
                    className="text-lg sm:text-xl md:text-2xl text-dark-300 max-w-xl mx-auto lg:mx-0 mb-6 sm:mb-10"
                  >
                    <TranslatedText 
                      i18nKey="hero.subtitle" 
                      type="fade"
                      duration={0.5}
                    />
                  </motion.p>

                  {/* CTA Buttons */}
                  <motion.div
                    variants={itemVariants}
                    className="flex flex-wrap justify-center lg:justify-start gap-4 mb-12 lg:mb-0 pointer-events-auto"
                  >
                    <Button
                      variant="primary"
                      size="lg"
                      icon={<ArrowRight className="w-5 h-5" />}
                      iconPosition="right"
                      onClick={() => setIsWizardOpen(true)}
                    >
                      <TranslatedText 
                        i18nKey="hero.cta" 
                        type="digital"
                        duration={0.5}
                      />
                    </Button>
                    {/* Video button hidden for now
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={enterVideoMode}
                      icon={<Play className="w-5 h-5" />}
                      iconPosition="left"
                    >
                      <TranslatedText 
                        i18nKey="hero.ctaSecondary" 
                        type="digital"
                        duration={0.5}
                      />
                    </Button>
                    */}
                  </motion.div>
                </div>

                {/* Right column - Iniesta with glitch effect */}
                <motion.div 
                  variants={itemVariants}
                  className="flex-1 w-full max-w-md lg:max-w-lg xl:max-w-xl"
                >
                  <IniestaGlitch 
                    className="w-full h-[300px] sm:h-[400px] md:h-[500px] lg:h-[600px]" 
                    showAppVersion={true}
                  />
                </motion.div>
              </div>

              {/* Stats Cards - FIFA Style - Full width below */}
              <motion.div
                variants={itemVariants}
                className="grid grid-cols-3 gap-4 md:gap-6 max-w-3xl mx-auto mt-12 lg:mt-16 pointer-events-auto"
              >
                {stats.map((stat) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + stat.delay }}
                    whileHover={{ scale: 1.05, y: -5 }}
                    className="relative group"
                  >
                    <div className="relative bg-brand-navy/80 backdrop-blur-xl border border-brand-lightBlue/30 rounded-xl p-4 md:p-6 overflow-hidden transition-all duration-300 group-hover:border-brand-yellow/50">
                      {/* Corner decorations */}
                      <CardCorner position="top-left" />
                      <CardCorner position="bottom-right" />
                      
                      {/* Icon */}
                      <div className="flex justify-center mb-3">
                        <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-brand-yellow/10 flex items-center justify-center group-hover:bg-brand-yellow/20 transition-colors">
                          <stat.icon className="w-5 h-5 md:w-6 md:h-6 text-brand-yellow" />
                        </div>
                      </div>
                      
                      {/* Value */}
                      <motion.div
                        className="text-3xl md:text-4xl font-black text-center text-brand-yellow"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.7 + stat.delay, type: 'spring' }}
                      >
                        {stat.value}
                      </motion.div>
                      
                      {/* Label */}
                      <div className="text-xs md:text-sm text-dark-400 text-center uppercase tracking-wider mt-1">
                        {stat.label}
                      </div>
                      
                      {/* Hover glow */}
                      <div className="absolute inset-0 bg-brand-yellow/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
      </motion.div>
        )}
      </AnimatePresence>

      {/* Scroll indicator */}
      <AnimatePresence>
        {!isHidden && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ delay: 1.5 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
          >
            <span className="text-xs text-dark-500 uppercase tracking-widest">Scroll</span>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <ChevronDown className="w-6 h-6 text-brand-yellow" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Side decorations */}
      <AnimatePresence>
        {!isHidden && (
          <>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, x: -20 }}
              className="absolute left-4 top-1/2 -translate-y-1/2 hidden lg:flex flex-col gap-4"
            >
              {[1, 2, 3].map((i) => (
                <motion.div
                  key={i}
                  className="w-1 h-12 bg-brand-yellow/20 rounded-full"
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: 1 }}
                  transition={{ delay: 0.5 + i * 0.1 }}
                />
              ))}
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, x: 20 }}
              className="absolute right-4 top-1/2 -translate-y-1/2 hidden lg:flex flex-col gap-4"
            >
              {[1, 2, 3].map((i) => (
                <motion.div
                  key={i}
                  className="w-1 h-12 bg-brand-yellow/20 rounded-full"
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: 1 }}
                  transition={{ delay: 0.5 + i * 0.1 }}
                />
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Registration Wizard */}
      <RegistrationWizard
        isOpen={isWizardOpen}
        onClose={() => setIsWizardOpen(false)}
        onSwitchToLogin={() => setIsLoginOpen(true)}
      />

      {/* Login Modal */}
      <LoginModal
        isOpen={isLoginOpen}
        onClose={() => setIsLoginOpen(false)}
        onSwitchToRegister={() => setIsWizardOpen(true)}
      />
    </section>
  );
}
